import { __assign, __extends } from "tslib";
import { applyPassive } from '@material/dom/events';
import { matches } from '@material/dom/ponyfill';
import { MDCRipple, MDCRippleFoundation } from '@material/ripple';
import { addClass, getShadowElement, removeClass } from '@tyler-components-web/core';
import { BaseAdapter } from '../core/adapters/base-adapter';
import { CHECKBOX_CONSTANTS } from './checkbox-constants';
var CheckboxAdapter = /** @class */ (function (_super) {
    __extends(CheckboxAdapter, _super);
    function CheckboxAdapter(component) {
        return _super.call(this, component) || this;
    }
    Object.defineProperty(CheckboxAdapter.prototype, "root_", {
        /// MDCRippleCapableSurface
        get: function () {
            return this._rootElement;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CheckboxAdapter.prototype, "unbounded", {
        get: function () {
            return true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CheckboxAdapter.prototype, "disabled", {
        get: function () {
            return this.isDisabled();
        },
        enumerable: true,
        configurable: true
    });
    CheckboxAdapter.prototype.initialize = function () {
        var _this = this;
        this._configureElements();
        this._attachInternalInputListeners();
        this._ripple = this._createRipple();
        requestAnimationFrame(function () {
            if (_this._ripple) {
                _this._ripple.layout();
            }
        });
    };
    CheckboxAdapter.prototype.setDense = function (value) {
        if (value) {
            this._setHostAttribute(CHECKBOX_CONSTANTS.attributes.DENSE);
            this.setRootClass(CHECKBOX_CONSTANTS.classes.CHECKBOX_DENSE);
        }
        else {
            this._removeHostAttribute(CHECKBOX_CONSTANTS.attributes.DENSE);
            this.removeRootClass(CHECKBOX_CONSTANTS.classes.CHECKBOX_DENSE);
        }
    };
    CheckboxAdapter.prototype.forceLayout = function () {
        this._ripple.layout();
    };
    CheckboxAdapter.prototype.isAttachedToDOM = function () {
        return Boolean(this._component.parentNode);
    };
    CheckboxAdapter.prototype._configureElements = function () {
        this._inputElement = this._component.querySelector(CHECKBOX_CONSTANTS.selectors.INPUT);
        this._rootElement = getShadowElement(this._component, CHECKBOX_CONSTANTS.selectors.ROOT);
        this._labelElement = this._component.querySelector(CHECKBOX_CONSTANTS.selectors.LABEL);
        if (this._labelElement) {
            this._labelElement.setAttribute(CHECKBOX_CONSTANTS.attributes.SLOT, CHECKBOX_CONSTANTS.selectors.LABEL);
        }
        if (this._inputElement) {
            this._inputElement.setAttribute(CHECKBOX_CONSTANTS.attributes.SLOT, CHECKBOX_CONSTANTS.selectors.INPUT);
        }
        addClass(CHECKBOX_CONSTANTS.classes.UPGRADED, this._rootElement);
    };
    CheckboxAdapter.prototype._setHostAttribute = function (name, value) {
        if (value === void 0) { value = ''; }
        this._component.setAttribute(name, value);
    };
    CheckboxAdapter.prototype._removeHostAttribute = function (name) {
        this._component.removeAttribute(name);
    };
    CheckboxAdapter.prototype.disconnect = function () {
        if (this._labelElement) {
            this._labelElement.removeAttribute(CHECKBOX_CONSTANTS.attributes.SLOT);
        }
        if (this._inputElement) {
            this._inputElement.removeAttribute(CHECKBOX_CONSTANTS.attributes.SLOT);
        }
        if (this._inputMutationObserver) {
            this._inputMutationObserver.disconnect();
        }
        this._detachInternalInputListeners();
        if (this._ripple) {
            this._ripple.destroy();
        }
    };
    CheckboxAdapter.prototype.setRootClass = function (classes) {
        if (this._rootElement) {
            addClass(classes, this._rootElement);
        }
    };
    CheckboxAdapter.prototype.setNativeAttribute = function (qualifiedName, value) {
        this._inputElement.setAttribute(qualifiedName, value);
    };
    CheckboxAdapter.prototype.isDisabled = function () {
        return this._inputElement.disabled;
    };
    CheckboxAdapter.prototype.isChecked = function () {
        return this._inputElement && this._inputElement.checked;
    };
    CheckboxAdapter.prototype.isIndeterminate = function () {
        return this._inputElement && this._inputElement.indeterminate;
    };
    CheckboxAdapter.prototype.removeNativeAttribute = function (qualifiedName) {
        this._inputElement.removeAttribute(qualifiedName);
    };
    CheckboxAdapter.prototype.removeRootClass = function (classes) {
        if (this._rootElement) {
            removeClass(classes, this._rootElement);
        }
    };
    CheckboxAdapter.prototype.installPropertyChangeHooks = function (callback) {
        var _this = this;
        var nativeCb = this._inputElement;
        var cbProto = Object.getPrototypeOf(nativeCb);
        CHECKBOX_CONSTANTS.CB_PROTO_PROPS.forEach(function (controlState) {
            var desc = Object.getOwnPropertyDescriptor(cbProto, controlState);
            // We have to check for this descriptor, since some browsers (Safari) don't support its return.
            // See: https://bugs.webkit.org/show_bug.cgi?id=49739
            if (!_this._validDescriptor(desc)) {
                return;
            }
            // Type cast is needed for compatibility with Closure Compiler.
            var nativeGetter = desc.get;
            if (desc) {
                var nativeCbDesc = {
                    configurable: desc.configurable,
                    enumerable: desc.enumerable,
                    get: nativeGetter,
                    set: function (state) {
                        desc.set.call(nativeCb, state);
                        callback();
                    }
                };
                Object.defineProperty(nativeCb, controlState, nativeCbDesc);
            }
        });
    };
    CheckboxAdapter.prototype.listen = function (qualifiedName, callback, onRoot) {
        if (onRoot === void 0) { onRoot = false; }
        if (onRoot) {
            this._rootElement.addEventListener(qualifiedName, callback);
        }
        else {
            this._inputElement.addEventListener(qualifiedName, callback);
        }
    };
    CheckboxAdapter.prototype.unlisten = function (qualifiedName, callback, onRoot) {
        if (onRoot === void 0) { onRoot = false; }
        if (!this._inputElement) {
            return;
        }
        if (onRoot) {
            this._rootElement.removeEventListener(qualifiedName, callback);
        }
        else {
            this._inputElement.removeEventListener(qualifiedName, callback);
        }
    };
    CheckboxAdapter.prototype.uninstallPropertyChangeHooks = function () {
        var _this = this;
        if (!this._inputElement) {
            return;
        }
        var nativeCb = this._inputElement;
        var cbProto = Object.getPrototypeOf(nativeCb);
        CHECKBOX_CONSTANTS.CB_PROTO_PROPS.forEach(function (controlState) {
            var desc = Object.getOwnPropertyDescriptor(cbProto, controlState);
            if (!_this._validDescriptor(desc)) {
                return;
            }
            Object.defineProperty(nativeCb, controlState, desc);
        });
    };
    CheckboxAdapter.prototype.setInputAttributeObserver = function (listener) {
        var _this = this;
        this._inputMutationObserver = new MutationObserver(function (mutations) {
            for (var _i = 0, mutations_1 = mutations; _i < mutations_1.length; _i++) {
                var mutation = mutations_1[_i];
                if (mutation.attributeName) {
                    listener(mutation.attributeName, _this._inputElement.getAttribute(mutation.attributeName));
                }
            }
        });
        this._inputMutationObserver.observe(this._inputElement, {
            attributes: true,
            attributeFilter: ['disabled', 'checked']
        });
    };
    CheckboxAdapter.prototype._validDescriptor = function (inputPropDesc) {
        return !!inputPropDesc && typeof inputPropDesc.set === 'function';
    };
    CheckboxAdapter.prototype._attachInternalInputListeners = function () {
        var _this = this;
        this._inputFocusHandler = function () { return _this._handleInputFocusListener(); };
        this._inputBlurHandler = function () { return _this._handleInputBlurListener(); };
        this._inputElement.addEventListener('focus', this._inputFocusHandler);
        this._inputElement.addEventListener('blur', this._inputBlurHandler);
    };
    CheckboxAdapter.prototype._detachInternalInputListeners = function () {
        removeEventListener('focus', this._inputFocusHandler);
        removeEventListener('blur', this._inputBlurHandler);
    };
    CheckboxAdapter.prototype._handleInputFocusListener = function () {
        addClass(CHECKBOX_CONSTANTS.classes.FOCUSED, this._rootElement);
    };
    CheckboxAdapter.prototype._handleInputBlurListener = function () {
        removeClass(CHECKBOX_CONSTANTS.classes.FOCUSED, this._rootElement);
    };
    CheckboxAdapter.prototype._createRipple = function () {
        var _this = this;
        var adapter = __assign(__assign({}, MDCRipple.createAdapter(this)), { deregisterInteractionHandler: function (evtType, handler) { return _this._inputElement.removeEventListener(evtType, handler, applyPassive()); }, isSurfaceActive: function () { return matches(_this._inputElement, ':active'); }, isUnbounded: function () { return Boolean(_this.unbounded); }, registerInteractionHandler: function (evtType, handler) { return _this._inputElement.addEventListener(evtType, handler, applyPassive()); }, isSurfaceDisabled: function () { return _this._inputElement.disabled; }, addClass: function (className) { return addClass(className, _this._rootElement); }, removeClass: function (className) { return removeClass(className, _this._rootElement); }, updateCssVariable: function (varName, value) { return _this._rootElement.style.setProperty(varName, value); } });
        var ripple = new MDCRipple(this._rootElement, new MDCRippleFoundation(adapter));
        return ripple;
    };
    return CheckboxAdapter;
}(BaseAdapter));
export { CheckboxAdapter };
